import type { ParkedDialog, ParkingState, SessionInternals } from '../model/types';

/**
 * Ищет дочерний XML-элемент по имени, независимо от namespace-префикса.
 *
 * @param root Корневой узел поиска.
 * @param name Локальное имя элемента.
 * @returns Найденный элемент или null.
 */
const pickElement = (root: Element, name: string): Element | null => {
	const direct = root.querySelector(name);
	if (direct) return direct;

	const all = root.querySelectorAll('*');
	for (let i = 0; i < all.length; i += 1) {
		const element = all.item(i);
		if (element?.localName === name) return element;
	}

	return null;
};

/**
 * Безопасно извлекает текст из дочернего XML-элемента.
 *
 * @param root Корневой узел.
 * @param selector Имя целевого дочернего элемента.
 * @returns Очищенная строка или пустая строка, если элемент отсутствует.
 */
const textContentOf = (root: Element | null, selector: string): string => {
	if (!root) return '';

	const el = pickElement(root, selector);
	const text = el?.textContent?.trim();
	return text?.length ? text : '';
};

/**
 * Извлекает целевой URI стороны диалога из узла local/remote.
 * Приоритет: `<target>` text -> `<target uri>` -> `<identity>`.
 *
 * @param partyNode Узел стороны диалога.
 * @returns URI стороны или пустая строка.
 */
const getPartyTarget = (partyNode: Element | null): string => {
	if (!partyNode) return '';

	const targetEl = pickElement(partyNode, 'target');

	// 1. Пробуем текст внутри <target>
	const targetText = targetEl?.textContent?.trim();
	if (targetText) return targetText;

	// 2. Пробуем атрибут uri
	const uri = targetEl?.getAttribute('uri')?.trim();
	if (uri) return uri;

	// 3. Фолбэк на <identity>
	return textContentOf(partyNode, 'identity');
};

/**
 * Читает `display`-атрибут участника из `<identity>`.
 *
 * @param partyNode Узел стороны диалога.
 * @returns Display name или undefined, если атрибут отсутствует.
 */
const getPartyDisplayName = (partyNode: Element | null): string | undefined => {
	if (!partyNode) return undefined;

	const identityEl = pickElement(partyNode, 'identity');
	const displayName = identityEl?.getAttribute('display')?.trim();

	return displayName?.length ? displayName : undefined;
};

/**
 * Собирает параметр Replaces из идентификаторов вызова и кодирует его.
 *
 * @param callId Call-ID исходного диалога.
 * @param fromTag Локальный SIP‑тег.
 * @param toTag Удалённый SIP‑тег.
 */
export const buildReplacesParam = (callId: string, fromTag: string, toTag: string) => {
	const raw = `${callId};from-tag=${fromTag};to-tag=${toTag}`;
	return encodeURIComponent(raw);
};

/**
 * Извлекает Call-ID из RTCSession.
 *
 * @param session Активная SIP‑сессия.
 * @returns Call-ID или undefined.
 */
export const getCallId = (session: SessionInternals): string | undefined => {
	return session._request?.call_id || session._dialog?.id?.call_id || session.id || undefined;
};

/**
 * Возвращает SIP‑теги локальной и удалённой стороны для сессии.
 *
 * @param session Активная SIP‑сессия.
 * @returns Объект вида { localTag, remoteTag }.
 */
export const getLocalRemoteTags = (
	session: SessionInternals
): { localTag?: string; remoteTag?: string } => {
	const isIncoming = session.direction === 'incoming';
	const localTag = isIncoming ? session._to_tag : session._from_tag;
	const remoteTag = isIncoming ? session._from_tag : session._to_tag;
	return { localTag, remoteTag };
};

/**
 * Возвращает URI контакта удалённой стороны.
 *
 * @param session Текущая SIP‑сессия.
 */
export const getRemoteContactUri = (session: SessionInternals): string => {
	return session._dialog?._remote_target?.toString() || '';
};

/** Заголовок Accept для подписки dialog-info (парковка). */
export const PARK_ACCEPT = 'application/dialog-info+xml';

/** Имя event-package парковки звонков. */
export const PARK_EVENT = 'dialog';

/**
 * Парсит XML dialog-info и возвращает массив припаркованных диалогов.
 *
 * @param xml Тело NOTIFY с dialog-info.
 */
export const parseDialogInfoXml = (xml?: string | null): ParkedDialog[] => {
	if (!xml) return [];

	let doc: Document;
	try {
		doc = new DOMParser().parseFromString(xml, 'application/xml');
	} catch {
		return [];
	}

	if (doc.querySelectorAll('parsererror').length > 0) {
		return [];
	}

	const dialogs = doc.querySelectorAll('dialog');
	const parsed: ParkedDialog[] = [];

	for (const dialog of dialogs) {
		const localNode = pickElement(dialog, 'local');
		const remoteNode = pickElement(dialog, 'remote');

		const directionAttr = dialog.getAttribute('direction');
		const direction =
			directionAttr === 'initiator' || directionAttr === 'recipient' ? directionAttr : 'initiator';

		const entry: ParkedDialog = {
			id: dialog.getAttribute('id') ?? '',
			callId: dialog.getAttribute('call-id') ?? '',
			localTag: dialog.getAttribute('local-tag') ?? '',
			remoteTag: dialog.getAttribute('remote-tag') ?? '',
			state: textContentOf(dialog, 'state') as ParkingState,
			direction,
			localTarget: getPartyTarget(localNode),
			remoteTarget: getPartyTarget(remoteNode),
			remoteDisplayName: getPartyDisplayName(remoteNode),
			startedWith: textContentOf(dialog, 'started-with'),
			rawXml: xml,
		};

		parsed.push(entry);
	}

	return parsed;
};

/**
 * Извлекает статус sipfrag из тела NOTIFY.
 *
 * @param body Текстовое тело sipfrag.
 * @returns Объект { code, reason } или null, если распарсить не удалось.
 */
export const parseSipfragStatus = (body?: string | null) => {
	if (!body) return null;

	const firstLine = body
		.split(/\r?\n/)
		.map((line) => line.trim())
		.find((line) => line.length > 0);

	if (!firstLine) return null;

	const match = firstLine.match(/^sip\/2\.0\s+(\d{3})\s*(.*)$/i);
	if (!match) return null;

	return {
		code: Number(match[1]),
		reason: match[2].trim(),
	};
};

/**
 * Парсит заголовок Subscription-State.
 *
 * @param value Значение заголовка.
 * @returns Объект с полями state, reason, retryAfter либо null.
 */
export const parseSubscriptionState = (value?: string | null) => {
	if (!value) return null;

	const parts = value
		.split(';')
		.map((p) => p.trim())
		.filter(Boolean);

	if (parts.length === 0) return null;

	const state = parts[0].toLowerCase();
	const params: Record<string, string> = {};

	for (let i = 1; i < parts.length; i += 1) {
		const [rawKey, rawValue = ''] = parts[i].split('=');
		if (!rawKey) continue;

		const key = rawKey.trim().toLowerCase();
		const cleaned = rawValue.trim().replaceAll(/^"|"$/g, '');

		params[key] = cleaned;
	}

	const retryAfterRaw = params['retry-after'];
	const retryAfter =
		typeof retryAfterRaw === 'string' && retryAfterRaw.length > 0
			? Number(retryAfterRaw)
			: undefined;

	return {
		state,
		reason: params.reason,
		retryAfter: Number.isFinite(retryAfter) ? retryAfter : undefined,
	};
};
